import React from "react";
import { User, Shield, Briefcase, Mail, Phone, MapPin, Tag, DollarSign, CheckCircle, Clock, AlertTriangle } from "lucide-react";
import { formatPhoneNumber } from "../../../utils/formatPhone";

interface WizardData {
  [key: string]: any;
}

interface TeamUser {
  _id: string;
  name: string;
  email: string;
}

interface ReviewStepProps {
  data: WizardData;
  teamUsers: TeamUser[];
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    passed: "bg-emerald-100 text-emerald-700",
    verified: "bg-emerald-100 text-emerald-700",
    not_started: "bg-slate-100 text-slate-600",
    not_required: "bg-slate-100 text-slate-600",
    pending: "bg-amber-100 text-amber-700",
    waived: "bg-amber-100 text-amber-700",
  };
  const icons: Record<string, React.FC<any>> = {
    passed: CheckCircle,
    verified: CheckCircle,
    not_started: Clock,
    not_required: Clock,
    pending: Clock,
    waived: AlertTriangle,
  };
  const Icon = icons[status] || Clock;
  const label = status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-slate-100 text-slate-600"}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
};

const ReviewStep: React.FC<ReviewStepProps> = ({ data, teamUsers }) => {
  const responsibleLawyer = teamUsers.find((u) => u._id === data.responsibleLawyerId);
  const typeLabels: Record<string, string> = { individual: "Individual", organization: "Organization", estate: "Estate" };
  const typeLabel = typeLabels[data.type] || data.type;
  const billingLabels: Record<string, string> = { hourly: "Hourly", flat: "Flat Fee", contingency: "Contingency", mixed: "Mixed" };
  const billingLabel = billingLabels[data.billingType] || data.billingType;

  const practiceLabels: Record<string, string> = {
    real_estate: "Real Estate", family: "Family", litigation: "Litigation",
    criminal: "Criminal", corporate: "Corporate", immigration: "Immigration",
    wills_estates: "Wills & Estates", other: "Other",
  };

  return (
    <div className="space-y-4">
      {/* Summary text */}
      <div className="text-center py-2">
        <p className="text-sm text-slate-600">
          This will create <span className="font-semibold text-slate-800">1 client</span>
          {data.createMatter && (
            <> and <span className="font-semibold text-slate-800">1 matter</span></>
          )}
        </p>
      </div>

      {/* Client Card */}
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <User className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">Client</h3>
          <span className="ml-auto text-xs text-slate-400">{typeLabel}</span>
        </div>
        <div className="px-4 py-3.5 space-y-2">
          <div className="text-base font-semibold text-slate-800">{data.displayName}</div>

          {!data.type || data.type !== "organization" ? (
            (data.firstName || data.lastName) && (
              <div className="text-sm text-slate-500">
                {data.firstName} {data.lastName}
              </div>
            )
          ) : (
            data.organizationName && (
              <div className="text-sm text-slate-500">{data.organizationName}</div>
            )
          )}

          <div className="flex flex-wrap gap-x-5 gap-y-1.5 pt-1">
            {data.email && (
              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                <Mail className="h-3.5 w-3.5 text-slate-400" />
                {data.email}
              </div>
            )}
            {data.phone && (
              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                <Phone className="h-3.5 w-3.5 text-slate-400" />
                {formatPhoneNumber(data.phone)}
              </div>
            )}
            {data.addressLine1 && (
              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                <MapPin className="h-3.5 w-3.5 text-slate-400" />
                {data.addressCity ? `${data.addressLine1}, ${data.addressCity}` : data.addressLine1}
              </div>
            )}
          </div>

          {data.tags.length > 0 && (
            <div className="flex items-center gap-1.5 pt-1">
              <Tag className="h-3.5 w-3.5 text-slate-400" />
              <div className="flex flex-wrap gap-1">
                {data.tags.map((tag: string) => (
                  <span key={tag} className="px-2 py-0.5 rounded-full bg-brand-light text-brand text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-slate-100 mt-2">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Shield className="h-3.5 w-3.5" />
              Conflict:
            </div>
            <StatusBadge status={data.conflictCheckStatus} />
            <div className="flex items-center gap-1.5 text-xs text-slate-500 ml-3">
              AML:
            </div>
            <StatusBadge status={data.amlStatus} />
          </div>
        </div>
      </div>

      {/* Matter Card */}
      {data.createMatter && (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-700">Matter</h3>
          </div>
          <div className="px-4 py-3.5 space-y-2">
            <div className="text-base font-semibold text-slate-800">{data.matterTitle}</div>

            <div className="flex flex-wrap gap-x-5 gap-y-1.5">
              <div className="text-sm text-slate-600">
                <span className="text-slate-400">Practice Area:</span>{" "}
                {practiceLabels[data.practiceArea] || data.practiceArea}
              </div>
              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                <DollarSign className="h-3.5 w-3.5 text-slate-400" />
                {billingLabel}
                {data.billingType === "flat" && data.flatFeeCents && (
                  <span className="text-slate-500"> -  ${Number(data.flatFeeCents).toLocaleString()}</span>
                )}
              </div>
            </div>

            {responsibleLawyer && (
              <div className="text-sm text-slate-600">
                <span className="text-slate-400">Responsible:</span>{" "}
                {responsibleLawyer.name}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewStep;
